import { setCookieValue, getCookieValue, deleteCookie } from '../shared/utils/cookie';

const URL_PARAMS_COOKIE = 'URL_PARAMS';

/**
 * An hour. The cookie exists to carry the landing's parameters across the
 * redirect and the sign-up that follows it, and a session cookie outlived that
 * by weeks: the city of a long-past visit kept seeding every later registration
 * in the same browser, whatever country the new chauffeur signed up from.
 */
const URL_PARAMS_TTL_DAYS = 1 / 24;

let initialParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());

if (Object.keys(initialParams).length > 0) {
  setCookieValue(URL_PARAMS_COOKIE, JSON.stringify(initialParams), URL_PARAMS_TTL_DAYS);
} else {
  initialParams = getCookieValue(URL_PARAMS_COOKIE) || {};
}

const persistParams = (params: Record<string, string>) => {
  if (Object.keys(params).length > 0) {
    setCookieValue(URL_PARAMS_COOKIE, JSON.stringify(params), URL_PARAMS_TTL_DAYS);
  } else {
    deleteCookie(URL_PARAMS_COOKIE);
  }
};

const queryParamsService = {
  getAllParams: () => initialParams,
  getParamsByNames(...names: string[]) {
    return Object.fromEntries(
      Object.entries(this.getAllParams()).filter(([name]) => names.includes(name)),
    );
  },
  getSingleByName(name: string) {
    return this.getAllParams()[name];
  },
  /**
   * The same read, but the parameter is struck from the cookie: what the landing
   * handed over seeds one registration, not every later one. It stays readable
   * in memory for the rest of this page's life, so a second read within the same
   * load — a re-dispatch, a remount — still sees the value it acted on.
   */
  consumeSingleByName(name: string) {
    const value = this.getSingleByName(name);

    if (value !== undefined) {
      persistParams(
        Object.fromEntries(Object.entries(initialParams).filter(([key]) => key !== name)),
      );
    }

    return value;
  },
};

export { queryParamsService };
